import React, { useEffect, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";
import { generateDays } from "../../../utils/Calendar";
import api from "../../../api/api";
import AssignEmployeeModal from "../AssignEmployeeModal/AssignEmployeeModal";
import AlertModal from "../AlertModal";
import UpdateEmployeeModal from "../UpdateEmployeeModal/UpdateEmployeeModal";

const classNames = (...classes) => {
  return classes.filter(Boolean).join(" ");
};

const convertDateToHourAndMinute = (date) => {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const MonthView = ({ setView }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [days, setDays] = useState([]);
  const [originalRoster, setOriginalRoster] = useState([]);
  const [assignEmployeeModalOpen, setAssignEmployeeModalOpen] = useState(false);
  const [updateEmployeeModalOpen, setUpdateEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      try {
        const response = await api.get(`/roster/byMonthAndYear/${month}/${year}`);
        const rosterData = response.data.map((roster) => ({
          id: roster.employeeId._id,
          rosterId: roster._id,
          name: `${roster.employeeId.firstName} ${roster.employeeId.lastName}`,
          shiftStart: roster.shiftStart,
          shiftEnd: roster.shiftEnd,
          datetime: roster.shiftStart,
          break: roster.breakTime,
          date: roster.date.split("T")[0],
        }));

        setOriginalRoster(rosterData);
        const newDays = generateDays(year, month - 1, rosterData);
        setDays(newDays);
        if (
          !selectedDay ||
          new Date(selectedDay.date).getMonth() !== month - 1 ||
          new Date(selectedDay.date).getFullYear() !== year
        ) {
          setSelectedDay(newDays.find((day) => day.isToday) || newDays[0]);
        }
      } catch (error) {
        console.error('Error fetching roster data:', error);
        const newDays = generateDays(year, month - 1);
        setDays(newDays);
        setSelectedDay(newDays.find((day) => day.isToday) || newDays[0]);
      }
    };

    fetchData();
  }, [currentDate]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasChanges) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    if (hasChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges]);

  const goToPreviousMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  const goToNextMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  const goToToday = () => setCurrentDate(new Date());

  const handleSave = async () => {
    // Flatten the days into an updated roster
    const updatedRoster = days.flatMap((day) =>
      day.employees.map((employee) => ({
        employeeId: employee.id,
        rosterId: employee.rosterId || null,
        date: employee.date,
        shiftStart: employee.shiftStart,
        shiftEnd: employee.shiftEnd,
        breakTime: employee.break,
      }))
    );
  
    // Detect changes by comparing with originalRoster
    const changes = updatedRoster.filter((updated) => {
      const original = originalRoster.find(
        (orig) => orig.id === updated.employeeId && orig.date === updated.date
      );
      // If no original exists (new employee) or any field differs, it's a change
      return (
        !original ||
        original.shiftStart !== updated.shiftStart ||
        original.shiftEnd !== updated.shiftEnd ||
        original.break !== updated.breakTime ||
        original.rosterId !== updated.rosterId // Include rosterId to catch new entries
      );
    });
  
    if (changes.length === 0) {
      setHasChanges(false);
      setAlertMessage("No changes to save!");
      return;
    }
  
    try {
      setHasChanges(false); // Reset before the API call
      const response = await api.put('/roster/', { rosters: updatedRoster });
      // console.log('API Response Data:', response.data);
  
      if (response.status >= 200 && response.status < 300) {
        // Update originalRoster with fresh server data
        const updatedRosterData = response.data.map((roster) => ({
          id: roster.employeeId._id,
          rosterId: roster._id,
          name: `${roster.employeeId.firstName} ${roster.employeeId.lastName}`,
          shiftStart: roster.shiftStart,
          shiftEnd: roster.shiftEnd,
          datetime: roster.shiftStart,
          break: roster.breakTime,
          date: roster.date.split("T")[0],
        }));
  
        // Fully replace originalRoster and regenerate days to ensure consistency
        setOriginalRoster(updatedRosterData);

        const newDays = generateDays(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          updatedRosterData
        );

        setDays(newDays);

        // Update selectedDay if it still matches the current date
        const newSelectedDay = newDays.find((day) => day.date === selectedDay?.date);
        if (newSelectedDay) {
          setSelectedDay(newSelectedDay);
        }
          
        // setAlertMessage("Roster saved successfully!");
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving rosters:', error.response || error.message || error);
      setAlertMessage("Failed to save rosters");
      setHasChanges(true); // Revert hasChanges if save fails
    }
  };

  const isPastWeek = (date) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    // startOfWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    // startOfWeek.setHours(0, 0, 0, 0);
    return new Date(date) < startOfWeek;
  };

  const handleAssignEmployeeClick = () => {
    if (isPastWeek(selectedDay.date)) {
      setAlertMessage("You cannot assign employees to past weeks!");
      return;
    }
    setAssignEmployeeModalOpen(true);
  };

  const handleAssignSubmit = (assignmentDetails) => {
    const updatedDays = days.map((day) => {
      if (day.date === selectedDay.date) {
        const employeeExists = day.employees.some(
          (employee) => employee.id === assignmentDetails.employee._id
        );

        if (employeeExists) {
          setAlertMessage("You cannot assign this employee again!");
          return day;
        }

        return {
          ...day,
          employees: [
            ...day.employees,
            {
              id: assignmentDetails.employee._id,
              name: `${assignmentDetails.employee.firstName} ${assignmentDetails.employee.lastName}`,
              date: assignmentDetails.date,
              shiftStart: new Date(assignmentDetails.date).setHours(
                assignmentDetails.shiftStart.split(':')[0],
                assignmentDetails.shiftStart.split(":")[1],
                0,
                0
              ),
              shiftEnd: new Date(assignmentDetails.date).setHours(
                assignmentDetails.shiftEnd.split(':')[0],
                assignmentDetails.shiftEnd.split(":")[1],
                0,
                0
              ),
              break: assignmentDetails.breakTime,
            },
          ],
        };
      }
      return day;
    });

    setDays(updatedDays);
    setHasChanges(true);
    setAssignEmployeeModalOpen(false);
  };

  const handleUpdateSubmit = (assignmentDetails) => {
    const updatedDays = days.map((day) => {
      if (day.date === selectedDay.date) {
        const updatedEmployees = day.employees.map((employee) => {
          if (employee.id === assignmentDetails.employee._id) {
            return {
              ...employee,
              shiftStart: new Date(assignmentDetails.date).setHours(
                assignmentDetails.shiftStart.split(':')[0],
                assignmentDetails.shiftStart.split(':')[1],
                0,
                0
              ),
              shiftEnd: new Date(assignmentDetails.date).setHours(
                assignmentDetails.shiftEnd.split(':')[0],
                assignmentDetails.shiftEnd.split(':')[1],
                0,
                0
              ),
              break: assignmentDetails.breakTime,
            };
          }
          return employee;
        });
        return { ...day, employees: updatedEmployees };
      }
      return day;
    });
    setDays(updatedDays);
    setHasChanges(true);
    setUpdateEmployeeModalOpen(false);

    if (selectedDay && selectedDay.date === assignmentDetails.date) {
      const updatedEmployees = selectedDay.employees.map((employee) => {
        if (employee.id === assignmentDetails.employee._id) {
          return {
            ...employee,
            shiftStart: new Date(assignmentDetails.date).setHours(
              assignmentDetails.shiftStart.split(':')[0],
              assignmentDetails.shiftStart.split(':')[1],
              0,
              0
            ),
            shiftEnd: new Date(assignmentDetails.date).setHours(
              assignmentDetails.shiftEnd.split(':')[0],
              assignmentDetails.shiftEnd.split(':')[1],
              0,
              0
            ),
            break: assignmentDetails.breakTime,
          };
        }
        return employee;
      });
      setSelectedDay({ ...selectedDay, employees: updatedEmployees });
    }
  };

  const handleEmployeeClick = (employee) => {
    if (isPastWeek(selectedDay.date)) {
      // setAlertMessage("You cannot assign employees to past weeks!");
      return;
    }
    setSelectedEmployee(employee);
    setUpdateEmployeeModalOpen(true);
  };

  const handleDragStart = (e, employeeId, fromDate) => {
    if (isPastWeek(fromDate)) {
      setAlertMessage("You cannot move employees to past weeks!");
      return;
    }
    e.dataTransfer.setData('employeeId', employeeId);
    e.dataTransfer.setData('fromDate', fromDate);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, toDate) => {
    e.preventDefault();
    const employeeId = e.dataTransfer.getData('employeeId');
    const fromDate = e.dataTransfer.getData('fromDate');

    if (fromDate === toDate) return;

    if (isPastWeek(toDate)) {
      setAlertMessage("You cannot move employees to past weeks!");
      return;
    }

    const toDay = days.find((day) => day.date === toDate);
    const employeeExistsInToDate = toDay?.employees.some((employee) => employee.id === employeeId);

    if (employeeExistsInToDate) {
      setAlertMessage("You cannot assign this employee again!");
      return;
    }

    const updatedDays = days.map((day) => {
      if (day.date === fromDate) {
        return { ...day, employees: day.employees.filter((employee) => employee.id !== employeeId) };
      }
      if (day.date === toDate) {
        const movedEmployee = days
          .find((d) => d.date === fromDate)
          .employees.find((employee) => employee.id === employeeId);

        if (movedEmployee) {
          return {
            ...day,
            employees: [...day.employees, { ...movedEmployee, date: toDate }],
          };
        }
      }
      return day;
    });

    setDays(updatedDays);
    setHasChanges(true);

    if (selectedDay && selectedDay.date === fromDate) {
      setSelectedDay({
        ...selectedDay,
        employees: selectedDay.employees.filter((employee) => employee.id !== employeeId),
      });
    } else if (selectedDay && selectedDay.date === toDate) {
      const movedEmployee = days
        .find((d) => d.date === fromDate)
        .employees.find((employee) => employee.id === employeeId);
      if (movedEmployee) {
        setSelectedDay({
          ...selectedDay,
          employees: [...selectedDay.employees, { ...movedEmployee, date: toDate }],
        });
      }
    }
  };

  const handleDeleteRoster = async (employee) => {
    const { rosterId, id: employeeId, date } = employee;

    if (isPastWeek(date)) {
      setAlertMessage("You cannot delete employees in past weeks!");
      return;
    }

    // If the roster has a rosterId, send a DELETE request to the backend
    if (rosterId) {
      try {
        const response = await api.delete(`/roster/${rosterId}`);
        // if (response.status >= 200 && response.status < 300) {
        //   setAlertMessage("Roster deleted successfully!");
        //   setTimeout(() => setAlertMessage(null), 2000);
        // } else {
        //   throw new Error(`Unexpected status code: ${response.status}`);
        // }
        setHasChanges(false)
      } catch (error) {
        console.error('Error deleting roster:', error.response || error.message || error);
        setAlertMessage("Failed to delete roster");
        return; // Exit if the API call fails
      }
    }

    // Remove the employee from days state
    const updatedDays = days.map((day) => {
      if (day.date === date) {
        return {
          ...day,
          employees: day.employees.filter((emp) => emp.id !== employeeId),
        };
      }
      return { ...day };
    });
    setDays(updatedDays);
    // setHasChanges(true); // Mark as changed since we modified the local state

    // Update selectedDay if it matches the deleted date
    if (selectedDay && selectedDay.date === date) {
      const updatedSelectedDay = {
        ...selectedDay,
        employees: selectedDay.employees.filter((emp) => emp.id !== employeeId),
      };
      setSelectedDay(updatedSelectedDay);
    }


  };

  return (
    <>
      <div className="lg:flex lg:h-full lg:flex-col">
        <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
          <h1 className="text-base font-semibold text-gray-900">
            <time dateTime={currentDate.toISOString().slice(0, 7)}>
              {currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
            </time>
          </h1>
          <div className="flex items-center">
            <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
              <button
                type="button"
                onClick={goToPreviousMonth}
                className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
              >
                <span className="sr-only">Previous month</span>
                <ChevronLeftIcon className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={goToToday}
                className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
              >
                Today
              </button>
              <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
              <button
                type="button"
                onClick={goToNextMonth}
                className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
              >
                <span className="sr-only">Next month</span>
                <ChevronRightIcon className="size-5" aria-hidden="true" />
              </button>
            </div>
            <div className="hidden md:ml-4 md:flex md:items-center">
              <Menu as="div" className="relative">
                <MenuButton
                  type="button"
                  className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Month view
                  <ChevronDownIcon
                    className="-mr-1 size-5 text-gray-400"
                    aria-hidden="true"
                  />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    {/* <MenuItem>
                      <span
                        onClick={() => setView("week")}
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer"
                      >
                        Week view 
                      </span>
                    </MenuItem> */}
                    <MenuItem>
                      <span className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer">
                        Month view
                      </span>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
              <div className="ml-6 h-6 w-px bg-gray-300" />
              <button
                type="button"
                onClick={() => handleAssignEmployeeClick()}
                disabled={selectedDay && isPastWeek(selectedDay.date)}
                className={classNames(
                  'ml-6 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
                  selectedDay && isPastWeek(selectedDay.date)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-500'
                )}
              >
                Assign employee
              </button>
              <button
                type="button"
                onClick={handleSave}
                className={`ml-6 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                  hasChanges
                    ? 'bg-green-600 hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                disabled={!hasChanges}
              >
                Save
              </button>
            </div>
            <Menu as="div" className="relative ml-6 md:hidden">
              <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                <span className="sr-only">Open menu</span>
                <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div className="py-1">
                  <MenuItem>
                    <span
                      onClick={handleAssignEmployeeClick}
                      disabled={selectedDay && isPastWeek(selectedDay.date)}
                      className={classNames(
                        'block px-4 py-2 text-sm text-gray-700 cursor-pointer',
                        selectedDay && isPastWeek(selectedDay.date)
                          ? 'opacity-50 cursor-not-allowed'
                          : 'data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none'
                      )}
                    >
                      Assign employee
                    </span>
                  </MenuItem>
                  <MenuItem>
                    <span
                      onClick={handleSave}
                      disabled={!hasChanges}
                      className={classNames(
                        'block px-4 py-2 text-sm text-gray-700 cursor-pointer',
                        !hasChanges
                          ? 'opacity-50 cursor-not-allowed'
                          : 'data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none'
                      )}
                    >
                      Save
                    </span>
                  </MenuItem>
                </div>
                <div className="py-1">
                  <MenuItem>
                    <span 
                    onClick={goToToday}
                    className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none cursor-pointer">
                      Go to today
                    </span>
                  </MenuItem>
                </div>
                <div className="py-1">
                  {/* <MenuItem>
                    <span className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer">
                      Week view
                    </span>
                  </MenuItem> */}
                  <MenuItem>
                    <span className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer">
                      Month view
                    </span>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>
        </header>

        <div className="shadow ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col">
          <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
            {weekdays.map((day, index) => (
              <div key={index} className="bg-white py-2">
                {day.charAt(0)}
                <span className="sr-only sm:not-sr-only">{day.slice(1)}</span>
              </div>
            ))}
          </div>
          <div className="flex bg-gray-200 text-xs/6 text-gray-700 lg:flex-auto">
            <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
              {days.map((day) => (
                <div
                  key={day.date}
                  onClick={() => setSelectedDay(day)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, day.date)}
                  className={classNames(
                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-500',
                    'relative px-3 py-2 cursor-pointer hover:bg-gray-100',
                    day.date === selectedDay?.date && 'ring-1 ring-indigo-600'
                  )}
                >
                  <time
                    dateTime={day.date}
                    className={
                      day.isToday
                        ? 'flex size-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white'
                        : undefined
                    }
                  >
                    {day.date.split('-').pop().replace(/^0/, '')}
                  </time>
                  {day.employees.length > 0 && (
                    <ol className="mt-2">
                      {day.employees.slice(0, 2).map((employee) => (
                        <li
                          key={employee.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, employee.id, day.date)}
                          className="group flex"
                          onClick={() => handleEmployeeClick(employee)}
                        >
                          <span className="flex w-full">
                            <p className="flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600">
                              {employee.name}
                            </p>
                            <time
                              dateTime={employee.date}
                              className="ml-3 hidden flex-none text-gray-500 group-hover:text-indigo-600 xl:block"
                            >
                              {convertDateToHourAndMinute(employee.shiftStart)} - {convertDateToHourAndMinute(employee.shiftEnd)}
                            </time>
                          </span>
                        </li>
                      ))}
                      {day.employees.length > 2 && (
                        <li className="text-gray-500">+ {day.employees.length - 2} more</li>
                      )}
                    </ol>
                  )}
                </div>
              ))}
            </div>
            <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
              {days.map((day) => (
                <button
                  key={day.date}
                  type="button"
                  onClick={() => setSelectedDay(day)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, day.date)}
                  className={classNames(
                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                    (day.date === selectedDay?.date || day.isToday) && 'font-semibold',
                    day.date === selectedDay?.date && 'text-white',
                    day.date !== selectedDay?.date && day.isToday && 'text-indigo-600',
                    day.date !== selectedDay?.date && day.isCurrentMonth && !day.isToday && 'text-gray-900',
                    day.date !== selectedDay?.date && !day.isCurrentMonth && !day.isToday && 'text-gray-500',
                    'flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10'
                  )}
                >
                  <time
                    dateTime={day.date}
                    className={classNames(
                      day.date === selectedDay?.date && 'flex size-6 items-center justify-center rounded-full',
                      day.date === selectedDay?.date && day.isToday && 'bg-indigo-600',
                      day.date === selectedDay?.date && !day.isToday && 'bg-gray-900',
                      'ml-auto'
                    )}
                  >
                    {day.date.split('-').pop().replace(/^0/, '')}
                  </time>
                  <span className="sr-only">{day.employees.length} employees</span>
                  {day.employees.length > 0 && (
                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                      {day.employees.map((employee) => (
                        <span key={employee.id} className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400" />
                      ))}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {selectedDay?.employees.length > 0 && (
          <div className="px-4 py-10 sm:px-6">
            <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow ring-1 ring-black/5">
              {selectedDay.employees.map((employee) => (
                <li
                  key={employee.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, employee.id, selectedDay.date)}
                  className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50 cursor-move"
                >
                  <div className="flex-auto">
                    <p className="font-semibold text-gray-900">{employee.name}</p>
                    <time dateTime={employee.date} className="mt-2 flex items-center text-gray-700">
                      <ClockIcon className="mr-2 size-5 text-gray-400" aria-hidden="true" />
                      {convertDateToHourAndMinute(employee.shiftStart)} - {convertDateToHourAndMinute(employee.shiftEnd)}
                    </time>
                    <p className="mt-2 text-gray-500">
                      Break: {employee.break} minutes
                    </p>
                  </div>

                  {
                    !isPastWeek(selectedDay.date) && (
                      <button
                        type="button"
                        className="ml-6 flex-none self-center rounded-md bg-white px-3 py-2 font-semibold text-gray-900 opacity-0 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400 focus:opacity-100 group-hover:opacity-100 cursor-pointer"
                        onClick={() => handleEmployeeClick(employee)}
                      >
                        Edit
                      </button>
                    )
                  }
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <AlertModal alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      <AssignEmployeeModal
        assignEmployeeModalOpen={assignEmployeeModalOpen}
        setAssignEmployeeModalOpen={setAssignEmployeeModalOpen}
        selectedDay={selectedDay}
        handleAssignSubmit={handleAssignSubmit}
      />
      <UpdateEmployeeModal
        updateEmployeeModalOpen={updateEmployeeModalOpen}
        setUpdateEmployeeModalOpen={setUpdateEmployeeModalOpen}
        selectedDay={selectedDay}
        handleUpdateSubmit={handleUpdateSubmit}
        selectedEmployee={selectedEmployee}
        handleDeleteRoster={handleDeleteRoster}
      />
    </>
  );
};

export default MonthView;